"use client";
import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { CartLine, CartProductSnapshot, Product } from "@/lib/types";
import { cartLineLimit, maxQuantity, parseCart, previewVariantId, resolveCart } from "@/lib/cart";
import { showSiteToast } from "./site-toast";
type CartContextValue = {
  products: Product[];
  lines: CartLine[];
  items: ReturnType<typeof resolveCart>;
  ready: boolean;
  message: string;
  add: (variantId: string, quantity: number, preview?: boolean, productData?: CartProductSnapshot) => void;
  update: (variantId: string, quantity: number) => void;
  clear: () => void;
  subtotal: number;
  count: number;
};
const Context = createContext<CartContextValue | null>(null);
// v3 intentionally resets the stuck cart and saves a compact display snapshot
// per selected line, instead of shipping the full 1,100+ product catalog to the browser.
const KEY = "nexo-cart-v3";
const retiredPreviewVariants = new Set([
  "twist-vase-default",
  "mushroom-lamp-default",
  "ribbed-planter-default",
  "desk-trio-default",
  "twist-vase-mini-default",
  "mushroom-lamp-mini-default",
  "ribbed-planter-large-default",
  "desk-trio-wide-default",
]);
type Snapshot = { lines: CartLine[]; ready: boolean; message: string };
const serverSnapshot: Snapshot = { lines: [], ready: false, message: "" };
// Only browser subscriptions/events mutate this store. SSR uses a constant empty snapshot.
let snapshot = serverSnapshot;
const listeners = new Set<() => void>();
function emit() {
  listeners.forEach((listener) => listener());
}
function readStorage() {
  try {
    const storedLines = parseCart(
      JSON.parse(localStorage.getItem(KEY) || "[]"),
    );
    const lines = storedLines.filter(
      (line) => !retiredPreviewVariants.has(line.variantId),
    );
    if (lines.length !== storedLines.length) {
      localStorage.setItem(KEY, JSON.stringify(lines));
    }
    snapshot = {
      ...snapshot,
      lines,
      ready: true,
      message:
        lines.length !== storedLines.length
          ? "کالاهای غیرقابل سفارش قدیمی از سبد حذف شدند."
          : snapshot.message,
    };
  } catch {
    snapshot = {
      ...snapshot,
      ready: true,
      message: "ذخیرهٔ قبلی سبد خوانده نشد. می‌توانید سبد تازه‌ای بسازید.",
    };
  }
  emit();
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!snapshot.ready) readStorage();
  const sync = (event: StorageEvent) => {
    if (event.key === KEY || event.key === null) readStorage();
  };
  window.addEventListener("storage", sync);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", sync);
  };
}
function save(lines: CartLine[], message = "") {
  try {
    localStorage.setItem(KEY, JSON.stringify(lines));
  } catch {
    message =
      "ذخیرهٔ سبد در این مرورگر ممکن نیست؛ سبد تا بستن صفحه حفظ می‌شود.";
  }
  snapshot = { lines, ready: true, message };
  emit();
}
export function CartProvider({
  products,
  children,
}: {
  products: Product[];
  children: ReactNode;
}) {
  const { lines, ready, message } = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => serverSnapshot,
  );
  const items = resolveCart(lines, products);
  useEffect(() => {
    if (!ready) return;
    let changed = false;
    const normalizedLines = snapshot.lines.map((line) => {
      if (!line.preview) return line;
      const product = products.find((candidate) => candidate.variants.some((variant) => variant.id === line.variantId)) ?? line.productSnapshot;
      const variant = product?.variants.find((candidate) => candidate.id === line.variantId);
      if (!product || !variant || product.demo || product.purchasable === false || maxQuantity(variant) < 1) return line;
      changed = true;
      return { variantId: line.variantId, quantity: Math.min(line.quantity, maxQuantity(variant)) };
    });
    if (changed) save(normalizedLines, "اطلاعات محصول به‌روز شد و سبد خرید دوباره آماده است.");
  }, [products, ready]);
  function update(variantId: string, quantity: number) {
    const line = snapshot.lines.find((l) => l.variantId === variantId);
    const item = resolveCart(line ? [line] : [], products)[0];
    const limit = item ? cartLineLimit(line!, item.variant) : 0;
    save(
      quantity <= 0
        ? snapshot.lines.filter((l) => l.variantId !== variantId)
        : snapshot.lines.map((l) =>
            l.variantId === variantId
              ? { ...l, quantity: Math.max(1, Math.min(quantity, limit)) }
              : l,
          ),
    );
  }
  function add(variantId: string, quantity: number, preview = false, productData?: CartProductSnapshot) {
    const suppliedProductMatches = Boolean(productData && (
      productData.variants.some((candidateVariant) => candidateVariant.id === variantId)
      || (preview && previewVariantId(productData.id) === variantId)
    ));
    const product = products.find((candidate) => candidate.variants.some((candidateVariant) => candidateVariant.id === variantId))
      ?? (preview && variantId.endsWith("::preview") ? products.find((candidate) => previewVariantId(candidate.id) === variantId) : undefined)
      ?? (suppliedProductMatches ? productData : undefined);
    const variant = product?.variants.find((candidate) => candidate.id === variantId);
    const isPreview = preview || !variant || !product || product.demo || product.purchasable === false || !variant.price;
    const resolvedVariantId = variantId || (product ? previewVariantId(product.id) : "");
    if (!resolvedVariantId || (!isPreview && (!variant || maxQuantity(variant) < 1))) return;
    const old = snapshot.lines.find((l) => l.variantId === resolvedVariantId);
    save(
      [
        ...snapshot.lines.filter((l) => l.variantId !== resolvedVariantId),
        {
          variantId: resolvedVariantId,
          quantity: Math.min(
            isPreview ? 10 : maxQuantity(variant!),
            (old?.quantity || 0) + quantity,
          ),
          ...(isPreview ? { preview: true } : {}),
          ...(product ? { productSnapshot: {
            id: product.id,
            slug: product.slug,
            name: product.name,
            subtitle: product.subtitle,
            images: product.images.slice(0, 2),
            variants: product.variants.slice(0, 20),
            basePrice: product.basePrice,
            oldPrice: product.oldPrice,
            demo: product.demo,
            purchasable: product.purchasable,
          } } : {}),
        },
      ],
      isPreview ? "این کالا در حال حاضر قابل سفارش نیست." : "محصول به سبد خرید اضافه شد.",
    );
    if (!isPreview) showSiteToast("محصول به سبد خرید اضافه شد.", "success");
  }
  function clear() {
    save([], "سبد خرید خالی شد.");
  }
  return (
    <Context.Provider
      value={{
        products,
        lines,
        items,
        ready,
        message,
        add,
        update,
        clear,
        subtotal: items.reduce((s, i) => s + i.variant.price * i.quantity, 0),
        count: lines.reduce((s, line) => s + line.quantity, 0),
      }}
    >
      {children}
      <div className="sr-only" role="status" aria-live="polite">
        {message}
      </div>
    </Context.Provider>
  );
}
export function useCart() {
  const value = useContext(Context);
  if (!value) throw new Error("CartProvider missing");
  return value;
}
